import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { API } from '../App';
import { ArrowUp, ArrowDown, ArrowUpDown, Loader2, ChevronRight, ChevronDown as ChevronDownIcon, Send, MessageSquare, Truck, StickyNote, Save } from 'lucide-react';
import OrderBookSendReviewModal from './OrderBookSendReviewModal';
import AddHOSupportClaimModal from './AddHOSupportClaimModal';

const enconOrange = '#ee7203';

const HO_SUPPORT_PILL_STYLE = {
  green:  'bg-green-100 text-green-700 border border-green-300',
  yellow: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  purple: 'bg-purple-100 text-purple-700 border border-purple-300',
  grey:   'bg-gray-100 text-gray-500 border border-gray-200',
};

const COLUMNS = [
  { key: 'customer_name',     label: 'Customer Name' },
  { key: 'order_number',      label: 'Order No.' },
  { key: 'bm_number',         label: 'BM No.' },
  { key: 'delivery_date',     label: 'Delivery Date' },
  { key: 'sales_order_value', label: 'Sales Order Value' },
  { key: 'contribution_pct',  label: 'Cont %' },
  { key: 'contribution_value', label: 'Cont Value' },
  { key: 'order_type',        label: 'Order Type' },
];

/** "01/01/27" (DD/MM/YY) → "01 Jan 2027" */
function formatOrderDate(str) {
  if (!str) return '—';
  const parts = str.split('/');
  if (parts.length !== 3) return str;
  const [d, m, y] = parts;
  const year = y.length === 2 ? `20${y}` : y;
  const dt = new Date(`${year}-${m}-${d}`);
  if (isNaN(dt.getTime())) return str;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const money = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Small grey "Y'day: <value>" sub-label shown under an amended cell so the
 * user can see at a glance what actually changed since the previous upload. */
function YesterdayNote({ children }) {
  return <div className="text-[11px] font-normal text-gray-400 mt-0.5">Y'day: {children}</div>;
}

/** Notes left by whoever answered a "Send for review" request on this order —
 * rendered as a full-width row spanning the whole line, under every filter view. */
function ReviewNotes({ notes }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {notes.map((n, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs font-normal text-blue-700" data-testid="order-book-review-note">
          <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span><span className="font-semibold">{n.author}:</span> {n.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Expandable row — per-product breakdown for one order, sourced from RTOB. */
function OrderLinesRow({ orderNumber, colSpan }) {
  const [lines, setLines] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`${API}/order-book-reports/order-lines/${orderNumber}`)
      .then((res) => { if (active) setLines(res.data?.lines || []); })
      .catch(() => { if (active) setLines([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orderNumber]);

  return (
    <tr className="bg-orange-50/30" data-testid={`order-book-lines-${orderNumber}`}>
      <td colSpan={colSpan} className="px-4 py-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading product lines…</div>
        ) : lines.length === 0 ? (
          <div className="text-xs text-gray-400 py-2">No product-level detail found for this order in the Daily Order Entry Report.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 uppercase text-[10px]">
                <th className="text-left py-1 pr-3">Product Code</th>
                <th className="text-left py-1 pr-3">Description</th>
                <th className="text-left py-1 pr-3">Qty</th>
                <th className="text-left py-1 pr-3">Line Value</th>
                <th className="text-left py-1 pr-3">Line Contribution</th>
                <th className="text-left py-1 pr-3">Line Cont %</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-orange-100">
                  <td className="py-1.5 pr-3 font-mono text-gray-600">{l.product_code || '—'}</td>
                  <td className="py-1.5 pr-3 text-gray-700">{l.product_description || '—'}</td>
                  <td className="py-1.5 pr-3 text-gray-600">{l.released_qty} {l.order_unit || ''}</td>
                  <td className="py-1.5 pr-3 text-gray-700">{money(l.line_value)}</td>
                  <td className="py-1.5 pr-3 text-gray-700">{money(l.line_contribution)}</td>
                  <td className="py-1.5 pr-3 text-gray-700">{(l.line_contribution_pct || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
}

/** Second, read-only table shown underneath Amendments — orders that were amended at some
 * earlier upload (so they're not part of the CURRENT amendments diff) but are still sitting
 * in the order book. Drops off on its own once the order leaves the order book. */
function HistoricalAmendmentsTable({ rows, onOpenCustomer, onOpenOrder }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 px-1 py-2 text-sm text-gray-500" data-testid="historical-amendments-heading">
        <span className="font-bold text-gray-800">Historical Amendments</span>
        <span className="text-xs text-gray-400">— amended previously, not amended again since, still in the order book</span>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="historical-amendments-table">
            <thead>
              <tr style={{ backgroundColor: '#94a3b8' }}>
                {COLUMNS.map((col) => (
                  <th key={col.key} className="px-4 py-2.5 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">{col.label}</th>
                ))}
                <th className="px-4 py-2.5 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">Last Amended</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const amended = new Set(o.amended_fields || []);
                const prevValues = o.amended_previous || {};
                return (
                  <tr key={o.order_number} className="border-t border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`historical-amendment-row-${o.order_number}`}>
                    <td className="px-4 py-2.5">
                      <button onClick={() => onOpenCustomer(o.customer_name)} className="font-semibold text-gray-700 hover:underline text-left" data-testid={`historical-amendment-customer-${o.order_number}`}>
                        {o.customer_name || '—'}
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => onOpenOrder(o.order_number)} className="font-mono font-semibold text-gray-600 hover:underline" data-testid={`historical-amendment-ordernum-${o.order_number}`}>
                        #{o.order_number}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-gray-500">{o.bm_number || '—'}</td>
                    <td className={`px-4 py-2.5 text-gray-600 ${amended.has('delivery_date') ? 'bg-yellow-50 font-semibold text-yellow-700' : ''}`}>
                      {formatOrderDate(o.delivery_date)}
                      {amended.has('delivery_date') && <YesterdayNote>{formatOrderDate(prevValues.delivery_date)}</YesterdayNote>}
                    </td>
                    <td className={`px-4 py-2.5 text-gray-600 ${amended.has('sales_order_value') ? 'bg-yellow-50 font-semibold text-yellow-700' : ''}`}>
                      {money(o.sales_order_value)}
                      {amended.has('sales_order_value') && <YesterdayNote>{money(prevValues.sales_order_value)}</YesterdayNote>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{(o.contribution_pct || 0).toFixed(2)}%</td>
                    <td className={`px-4 py-2.5 text-gray-600 ${amended.has('contribution_value') ? 'bg-yellow-50 font-semibold text-yellow-700' : ''}`}>
                      {money(o.contribution_value)}
                      {amended.has('contribution_value') && <YesterdayNote>{money(prevValues.contribution_value)}</YesterdayNote>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-semibold px-2 py-1 rounded-full" style={o.delivery_type === 'Direct' ? { background: '#fee2e2', color: '#b91c1c' } : { background: '#dbeafe', color: '#1d4ed8' }}>
                        {o.delivery_type || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">
                      {o.amended_detected_at ? new Date(o.amended_detected_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Standalone per-line note box — expandable textarea + Save, under the row
 * (and under the product-lines drop-down too, if that's also open — never
 * above/replacing it). Icon in the row turns yellow once `note` is non-empty. */
function OrderNoteBox({ orderNumber, note, colSpan, onSaved }) {
  const [text, setText] = useState(note?.text || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`${API}/order-book-reports/notes/${orderNumber}`, { text });
      onSaved(orderNumber, res.data?.note || null);
    } catch {
      // no-op — Save button stays enabled to retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="bg-yellow-50/40" data-testid={`order-book-note-box-row-${orderNumber}`}>
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="flex items-start gap-2">
          <StickyNote className="w-4 h-4 text-yellow-600 mt-2 flex-shrink-0" />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a note for this order…"
            rows={2}
            className="flex-1 border border-yellow-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-yellow-300 resize-none"
            data-testid={`order-book-note-textarea-${orderNumber}`}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
            style={{ backgroundColor: '#ca8a04' }}
            data-testid={`order-book-note-save-${orderNumber}`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Order Book table for the BI Reports page — sortable columns, click-through
 * to the CRM customer search modal (customer name) and the parsed order
 * document modal (order number), row selection + "Send for review" workflow,
 * per-order product drill-down (RTOB), plus contextual highlighting for the
 * Amendments / Transport Info filters.
 */
export default function OrderBookTable({ filter, lowContributionPct = 6, month = 'all', branchId = 'C30', onOpenCustomer, onOpenOrder }) {
  const [orders, setOrders] = useState([]);
  const [historicalAmendments, setHistoricalAmendments] = useState([]);
  const [summary, setSummary] = useState({ total_orders: 0, total_value: 0, total_contribution: 0 });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('delivery_date');
  const [sortDir, setSortDir] = useState('asc');
  const [expanded, setExpanded] = useState(new Set());
  const [openNotes, setOpenNotes] = useState(new Set());
  const [selected, setSelected] = useState({}); // order_number -> row
  const [showSendModal, setShowSendModal] = useState(false);
  const [claimModalOrder, setClaimModalOrder] = useState(null);

  // When switching into the Transport Info view, default to sorting by the
  // Actual Date column so orders not yet on transport sink to the bottom.
  useEffect(() => {
    if (filter === 'transport_info') {
      setSortBy('transport_date');
      setSortDir('asc');
    }
  }, [filter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/order-book-reports/order-book`, {
        params: { filter, sort_by: sortBy, sort_dir: sortDir, low_contribution_pct: lowContributionPct, month },
      });
      setOrders(res.data?.orders || []);
      setHistoricalAmendments(res.data?.historical_amendments || []);
      setSummary(res.data?.summary || { total_orders: 0, total_value: 0, total_contribution: 0 });
    } catch {
      setOrders([]);
      setHistoricalAmendments([]);
    } finally {
      setLoading(false);
    }
  }, [filter, sortBy, sortDir, lowContributionPct, month]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const toggleExpand = (orderNumber) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber); else next.add(orderNumber);
      return next;
    });
  };

  const toggleNoteBox = (orderNumber) => {
    setOpenNotes(prev => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber); else next.add(orderNumber);
      return next;
    });
  };

  const handleNoteSaved = (orderNumber, note) => {
    setOrders(prev => prev.map(o => (o.order_number === orderNumber ? { ...o, note } : o)));
  };

  const toggleSelect = (order) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[order.order_number]) delete next[order.order_number];
      else next[order.order_number] = order;
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;

  const SortIcon = ({ colKey }) => {
    if (sortBy !== colKey) return <ArrowUpDown className="w-3 h-3 text-white/40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-white" /> : <ArrowDown className="w-3 h-3 text-white" />;
  };

  const showActualDate = filter === 'transport_info';
  const colSpan = COLUMNS.length + 4 + (showActualDate ? 1 : 0);

  return (
    <div>
      {/* Overview line */}
      <div className="flex items-center gap-4 px-1 py-3 text-sm text-gray-600" data-testid="order-book-overview">
        <span><span className="font-bold text-gray-900">{summary.total_orders}</span> Orders</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-gray-900">{money(summary.total_value)}</span> Total Value</span>
        <span className="text-gray-300">·</span>
        <span><span className="font-bold text-gray-900">{money(summary.total_contribution)}</span> Total Contribution</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="order-book-table">
            <thead>
              <tr style={{ backgroundColor: enconOrange }}>
                <th className="px-3 py-3 whitespace-nowrap">
                  <button
                    onClick={() => setShowSendModal(true)}
                    disabled={selectedCount === 0}
                    data-testid="order-book-send-for-review-btn"
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                      selectedCount > 0 ? 'bg-white text-orange-600 hover:bg-orange-50 shadow-sm' : 'bg-black/10 text-white/50 cursor-not-allowed'
                    }`}
                  >
                    <Send className="w-3.5 h-3.5" /> Send{selectedCount > 0 ? ` (${selectedCount})` : ''}
                  </button>
                </th>
                <th className="px-2 py-3" />
                <th className="px-2 py-3" />
                <th className="px-2 py-3" />
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleSort(col.key)}
                    className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide cursor-pointer hover:bg-black/10 whitespace-nowrap select-none"
                    data-testid={`order-book-sort-${col.key}`}
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      <SortIcon colKey={col.key} />
                    </div>
                  </th>
                ))}
                {showActualDate && (
                  <th
                    onClick={() => toggleSort('transport_date')}
                    className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide cursor-pointer hover:bg-black/10 whitespace-nowrap select-none"
                    style={{ backgroundColor: '#1d4ed8' }}
                    data-testid="order-book-sort-transport_date"
                  >
                    <div className="flex items-center gap-1.5">
                      Actual Date
                      <SortIcon colKey="transport_date" />
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colSpan} className="text-center py-14"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: enconOrange }} /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={colSpan} className="text-center py-14 text-gray-400">No orders match this view</td></tr>
              ) : (
                orders.map((o) => {
                  const amended = new Set(o.amended_fields || []);
                  const prevValues = o.amended_previous || {};
                  const isExpanded = expanded.has(o.order_number);
                  const isSelected = !!selected[o.order_number];
                  return (
                    <React.Fragment key={o.order_number}>
                      <tr className={`border-t border-gray-100 hover:bg-orange-50/40 transition-colors ${isSelected ? 'bg-orange-50/60' : ''}`} data-testid={`order-book-row-${o.order_number}`}>
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(o)}
                            className="w-4 h-4 accent-orange-500 cursor-pointer"
                            data-testid={`order-book-checkbox-${o.order_number}`}
                          />
                        </td>
                        <td className="px-2 py-3">
                          <button
                            onClick={() => setClaimModalOrder(o.order_number)}
                            className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 whitespace-nowrap transition-transform hover:scale-105 ${HO_SUPPORT_PILL_STYLE[o.ho_support_status] || HO_SUPPORT_PILL_STYLE.grey}`}
                            title="Add / view H/O Support claim for this order"
                            data-testid={`order-book-ho-support-pill-${o.order_number}`}
                          >
                            H/O £
                          </button>
                        </td>
                        <td className="px-2 py-3">
                          <button
                            onClick={() => toggleNoteBox(o.order_number)}
                            className={o.note ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-300 hover:text-gray-500'}
                            title={o.note ? `Note: ${o.note.text}` : 'Add a note'}
                            data-testid={`order-book-note-icon-${o.order_number}`}
                          >
                            <StickyNote className="w-4 h-4" fill={o.note ? 'currentColor' : 'none'} />
                          </button>
                        </td>
                        <td className="px-2 py-3">
                          <button
                            onClick={() => toggleExpand(o.order_number)}
                            className="text-gray-400 hover:text-gray-700"
                            data-testid={`order-book-expand-${o.order_number}`}
                          >
                            {isExpanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => onOpenCustomer(o.customer_name)}
                            className="font-semibold text-gray-800 hover:underline text-left"
                            style={{ color: enconOrange }}
                            data-testid={`order-book-customer-${o.order_number}`}
                          >
                            {o.customer_name || '—'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => onOpenOrder(o.order_number)}
                            className="font-mono font-semibold text-gray-700 hover:underline"
                            data-testid={`order-book-ordernum-${o.order_number}`}
                          >
                            #{o.order_number}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-600" data-testid={`order-book-bmnum-${o.order_number}`}>
                          {o.bm_number || '—'}
                        </td>
                        <td className={`px-4 py-3 text-gray-600 ${amended.has('delivery_date') ? 'bg-yellow-100 font-semibold text-yellow-800' : ''}`}>
                          <span className="inline-flex items-center gap-1.5">
                            {formatOrderDate(o.delivery_date)}
                            {o.date_moved && (
                              <Truck
                                className="w-3.5 h-3.5 text-blue-600 flex-shrink-0"
                                data-testid={`date-moved-icon-${o.order_number}`}
                                title="This order's delivery date was auto-moved on Transport after a BI Report date change — click the row to see the full history"
                              />
                            )}
                          </span>
                          {amended.has('delivery_date') && <YesterdayNote>{formatOrderDate(prevValues.delivery_date)}</YesterdayNote>}
                        </td>
                        <td className={`px-4 py-3 text-gray-700 ${amended.has('sales_order_value') ? 'bg-yellow-100 font-semibold text-yellow-800' : ''}`}>
                          {money(o.sales_order_value)}
                          {amended.has('sales_order_value') && <YesterdayNote>{money(prevValues.sales_order_value)}</YesterdayNote>}
                        </td>
                        <td className={`px-4 py-3 ${(o.contribution_pct || 0) < lowContributionPct ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                          {(o.contribution_pct || 0).toFixed(2)}%
                        </td>
                        <td className={`px-4 py-3 text-gray-700 ${amended.has('contribution_value') ? 'bg-yellow-100 font-semibold text-yellow-800' : ''}`}>
                          {money(o.contribution_value)}
                          {amended.has('contribution_value') && <YesterdayNote>{money(prevValues.contribution_value)}</YesterdayNote>}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="text-xs font-semibold px-2 py-1 rounded-full"
                            style={o.delivery_type === 'Direct'
                              ? { background: '#fee2e2', color: '#b91c1c' }
                              : { background: '#dbeafe', color: '#1d4ed8' }}
                          >
                            {o.delivery_type || '—'}
                          </span>
                        </td>
                        {showActualDate && (
                          <td className="px-4 py-3 bg-blue-50 font-semibold text-blue-700">
                            {o.transport_date ? new Date(o.transport_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not on transport'}
                          </td>
                        )}
                      </tr>
                      {o.review_notes && o.review_notes.length > 0 && (
                        <tr className="bg-blue-50/40" data-testid={`order-book-notes-row-${o.order_number}`}>
                          <td colSpan={colSpan} className="px-4 py-2">
                            <ReviewNotes notes={o.review_notes} />
                          </td>
                        </tr>
                      )}
                      {isExpanded && <OrderLinesRow orderNumber={o.order_number} colSpan={colSpan} />}
                      {openNotes.has(o.order_number) && (
                        <OrderNoteBox
                          orderNumber={o.order_number}
                          note={o.note}
                          colSpan={colSpan}
                          onSaved={handleNoteSaved}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {filter === 'amendments' && (
        <HistoricalAmendmentsTable rows={historicalAmendments} onOpenCustomer={onOpenCustomer} onOpenOrder={onOpenOrder} />
      )}

      <OrderBookSendReviewModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        branchId={branchId}
        selectedOrders={Object.values(selected)}
        onSent={() => { setSelected({}); setShowSendModal(false); }}
      />

      <AddHOSupportClaimModal
        isOpen={!!claimModalOrder}
        orderNumber={claimModalOrder}
        onClose={() => setClaimModalOrder(null)}
        onAdded={load}
      />
    </div>
  );
}
